import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { ImportJobDto, ResponseDto } from "@mimber/schema"
import type { CreateImportJobVariables } from "./useCreateImportJob.types"

/**
 * Uploads a CSV file + column mapping to start a bulk import job. Processing
 * is asynchronous — the response is the created (PENDING) job, not the
 * final result. The admin is emailed once the background job completes.
 */
export const useCreateImportJob = (
  options?: Omit<
    UseMutationOptions<
      ResponseDto<ImportJobDto>,
      Error,
      CreateImportJobVariables
    >,
    "mutationFn"
  >
): UseMutationResult<
  ResponseDto<ImportJobDto>,
  Error,
  CreateImportJobVariables
> => {
  guardAppUsage("mimber-organization", "useCreateImportJob")

  const { server } = useServerContext()

  return useMutation({
    ...options,
    mutationFn: async ({ file, type, columnMapping, options: jobOptions }) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("type", type)
      formData.append("columnMapping", JSON.stringify(columnMapping))
      formData.append("options", JSON.stringify(jobOptions ?? {}))

      const response = await server.post<ResponseDto<ImportJobDto>>(
        "imports",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      return response.data
    },
  })
}
